// EXTERNAL DEPENDENCIES
import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

// HELPERS
import { recordImpression, scrollToTop, trackImpression } from '../../utils'
import { useProfileData } from '../../api/profile'
import { useDispatch } from 'react-redux'
import {
  getEventNameAndType,
  isNotFoundError,
} from '../../utils/common-helpers'
import { useBusinessProfileReduxState } from './hooks'

// REDUX
import {
  updateEditPageErrorMessage,
  updateProfileData,
} from '../../redux/features'

// COMPONENTS
import { Alert, Row } from '@paypalcorp/pp-react'
import Layout from '../../common/components/layout'
import {
  EditProfilePage,
  HeaderSection,
  PreviewProfilePage,
  ViewProfilePage,
  OwnerProfilePage,
} from './components'
import ErrorPage from '../error-page'

// FPTI
import { fptiMetadata } from './constants/fpti-constants'
import { EDIT_BP_ABANDON_POPUP_SHOWN } from './components/edit-profile-page/fpti'
import { BUSINESS_PROFILE_PREVIEW_PAGE_SHOWN_FPTI } from './fpti'

// CONSTANTS
import serverData from '@/utils/server-data'

// STYLES
import styles from './styles.module.less'

/**
 * The Business Profile Page allows users to view business profiles and edit their own business profile
 */
export default function BusinessProfilePage() {
  // DECLARATIONS
  const dispatch = useDispatch()
  const { pathname } = useLocation()

  const { userCanPreviewProfile, userCanViewModularRefactor } =
    serverData.elmo || {}

  // STATE
  const [isOwnerEditing, setIsOwnerEditing] = useState(false)
  const {
    isPreviewing,
    editedProfileData: { areValuesEdited, errorMessage, isEditing },
  } = useBusinessProfileReduxState()

  // NETWORK REQUESTS
  const { handle } = useParams()
  const { data: profileData, error } = useProfileData(handle)

  // set profile data
  useEffect(() => {
    if (profileData) {
      dispatch(updateProfileData(profileData))
    }
  }, [dispatch, profileData])

  useEffect(() => {
    if (!userCanViewModularRefactor) {
      const { userLoggedIn, canEdit } = profileData || {}
      const { eventType, eventName } = getEventNameAndType(
        canEdit,
        userLoggedIn,
        fptiMetadata.BP_VIEWED,
      )

      recordImpression({
        eventName,
        eventType,
        handle: profileData?.handle,
        payerId: profileData?.payerId,
        consumerId: profileData?.consumerId,
      })
    }
  }, [profileData, userCanViewModularRefactor])

  useEffect(() => {
    const isEditingRegex = /\/edit\//i

    setIsOwnerEditing(isEditingRegex.test(pathname))
  }, [pathname])

  useEffect(() => {
    if (errorMessage) {
      scrollToTop()
    }
  }, [errorMessage])

  window.onbeforeunload = (e) => {
    if (process.env.NODE_ENV === 'production' && areValuesEdited) {
      trackImpression(EDIT_BP_ABANDON_POPUP_SHOWN)
      e.preventDefault()
      e.returnValue = true
    }
  }

  if (error) {
    const profileNotFound = isNotFoundError(error)

    return (
      <ErrorPage
        profileDataNotFound={profileNotFound}
        reloadPage={!profileNotFound}
      />
    )
  }

  return (
    <>
      <HeaderSection
        shouldShow={
          !isEditing && !isPreviewing && profileData?.canEdit && !isOwnerEditing
        }
        userCanCreateProfile={
          profileData?.userCanCreateProfile && !userCanViewModularRefactor
        }
        userCanPreviewProfile={userCanPreviewProfile}
        profilePublished={profileData?.profilePreferences?.profilePublished}
      />
      <Layout>
        {errorMessage && (
          <Row align='center'>
            <Alert
              type='error'
              closeButton={{
                onClick: () => {
                  dispatch(updateEditPageErrorMessage(''))
                },
              }}
              className={styles.profilePageAlert}
            >
              {errorMessage}
            </Alert>
          </Row>
        )}
        {userCanViewModularRefactor && profileData?.canEdit ? (
          <OwnerProfilePage />
        ) : (
          <>
            {!isEditing && !isPreviewing ? <ViewProfilePage /> : null}
            {isEditing && <EditProfilePage />}
            {isPreviewing && (
              <PreviewProfilePage
                impressionToTrack={BUSINESS_PROFILE_PREVIEW_PAGE_SHOWN_FPTI}
                userCanCreateProfile={profileData?.userCanCreateProfile}
                userCanEdit={profileData?.canEdit}
              />
            )}
          </>
        )}
      </Layout>
    </>
  )
}
